import React, { useCallback } from 'react';

import { SAL2110_R01Filter, useSAL2110_R01Store } from 'src/containers/mes/SAL/SAL2110_R01/Store_SAL2110_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';

import { getSAL2110_R01AllCount, getSAL2110_R01List, downloadExcel_SAL2110_R01 } from "./Api_SAL2110_R01";
import { SAL2110_R01Dto } from './Dto_SAL2110_R01';
import { AgGridReact } from 'ag-grid-react';
import { downloadExcel } from "src/utils/blob/blobUtil";
import { aggregateBrandTotalList, aggregateCustomTotalList, aggregateItemTotalList } from './utils/aggregateUtils';

interface Props {
	customGridRef: React.RefObject<AgGridReact<SAL2110_R01Dto> | null>;
	brandGridRef: React.RefObject<AgGridReact<SAL2110_R01Dto> | null>;
	itemGridRef: React.RefObject<AgGridReact<SAL2110_R01Dto> | null>;
	setMessage: (message: string) => void;
}

export const Content_SAL2110_R01 = (props: Props) => {
	const { setMessage } = props;

	const {
		SAL2110_R01Filter,
		setSAL2110_R01PastFilter,
		SAL2110_R01CustomTotalList, setSAL2110_R01CustomTotalList,
		SAL2110_R01BrandTotalList, setSAL2110_R01BrandTotalList,
		SAL2110_R01ItemTotalList, setSAL2110_R01ItemTotalList,
		setAllCount,
		setCustomTotalScrollTop, setBrandTotalScrollTop, setItemTotalScrollTop,
	} = useSAL2110_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth("SAL2110_R01");

	const getParams = (filter: SAL2110_R01Filter) => {
		const params: Partial<SAL2110_R01Filter> = {
			compCd: filter.compCd || undefined,
			dateRange: filter.dateRange || undefined,
			customCd: filter.customCd || undefined,
			itemCd: filter.itemCd || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getSAL2110_R01AllCount(getParams(SAL2110_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [SAL2110_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setCustomTotalScrollTop(null);
		setBrandTotalScrollTop(null);
		setItemTotalScrollTop(null);
		setLoadingSpinner(true);

		const params = getParams(SAL2110_R01Filter);
		const responseData = await getSAL2110_R01List('search', params, 0);
		const detailRows = [...(responseData.data.data ?? [])] as SAL2110_R01Dto[];

		const customList = aggregateCustomTotalList(detailRows);
		const brandList = aggregateBrandTotalList(detailRows);
		const itemList = aggregateItemTotalList(detailRows);

		setSAL2110_R01CustomTotalList(customList);
		setSAL2110_R01BrandTotalList(brandList);
		setSAL2110_R01ItemTotalList(itemList);

		const processAllCount = await renewalAllCount();

		if (detailRows.length > 0) {
			setSAL2110_R01PastFilter(SAL2110_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${detailRows.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSAL2110_R01CustomTotalList([]);
			setSAL2110_R01BrandTotalList([]);
			setSAL2110_R01ItemTotalList([]);
		}
		setLoadingSpinner(false);
	}, [
		SAL2110_R01Filter,
		setSAL2110_R01CustomTotalList,
		setSAL2110_R01BrandTotalList,
		setSAL2110_R01ItemTotalList,
		setSAL2110_R01PastFilter,
		setLoadingSpinner,
		setMessage,
		renewalAllCount,
		setCustomTotalScrollTop,
		setBrandTotalScrollTop,
		setItemTotalScrollTop,
	]);

	const handleExcelClick = useCallback(async () => {
		const hasData =
			SAL2110_R01CustomTotalList.length > 0
			|| SAL2110_R01BrandTotalList.length > 0
			|| SAL2110_R01ItemTotalList.length > 0;

		if (!hasData) {
			showWarning("엑셀 다운로드할 데이터가 없습니다.");
			return;
		}

		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_SAL2110_R01('search', getParams(SAL2110_R01Filter));

			const binaryString = atob(response.data.data);
			const byteArray = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				byteArray[i] = binaryString.charCodeAt(i);
			}
			const blob = new Blob([byteArray], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});

			downloadExcel(blob, '기간별 매출현황 조회.xlsx');

			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('엑셀 다운로드 실패:', error);
			showError('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [SAL2110_R01CustomTotalList, SAL2110_R01BrandTotalList, SAL2110_R01ItemTotalList, SAL2110_R01Filter, setLoadingSpinner]);

	return {
		fetchData,
		handleExcelClick,
	};
}
